import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { MaterialQuestion } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Training/Courses/Lessons/PathItems';

interface QuestionViewProps {
  open: boolean;
  onClose: () => void;
  onSave: (q: MaterialQuestion) => void;
  initial?: MaterialQuestion;
}

const QuestionView: React.FC<QuestionViewProps> = ({ open, onClose, onSave, initial }) => {
  const [type, setType] = useState(initial?.type || 'yesno');
  const [question, setQuestion] = useState(initial?.question || '');
  // For Yes/No
  const [yesNoAnswer, setYesNoAnswer] = useState(initial?.validationOptions.ifYesNo ?? false);
  // For Multiple choice
  const [choices, setChoices] = useState(initial?.typeOptions.ifMultipleChoice ?? []);
  const [correctChoice, setCorrectChoice] = useState(initial?.validationOptions.ifMultipleChoice ?? 0);

  useEffect(() => {
    setType(initial?.type || 'yesno');
    setQuestion(initial?.question || '');
    setYesNoAnswer(initial?.validationOptions.ifYesNo ?? false);
    setChoices(initial?.typeOptions.ifMultipleChoice ?? []);
    setCorrectChoice(initial?.validationOptions.ifMultipleChoice ?? 0);
  }, [open, initial]);

  const handleChoiceChange = (idx: number, value: string) => {
    const newChoices = [...choices];
    newChoices[idx] = value;
    setChoices(newChoices);
  };
  const handleAddChoice = () => {
    setChoices([...choices, '']);
  };
  const handleRemoveChoice = (idx: number) => {
    setChoices(choices.filter((_: string, i: number) => i !== idx));
    if (correctChoice === idx) setCorrectChoice(0);
    else if (correctChoice > idx) setCorrectChoice(correctChoice - 1);
  };

  const handleSave = () => {
    if (!question.trim()) return;
    if (type === 'multiplechoice' && choices.some((c: string) => !c.trim())) return;
    onSave({
      id: initial?.id || '',
      type,
      question,
      typeOptions: {
        ifMultipleChoice: type === 'multiplechoice' ? choices : [],
      },
      validationOptions: {
        ifYesNo: yesNoAnswer ?? false,
        ifMultipleChoice: correctChoice ?? 0,
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Question
        <IconButton onClick={onClose} size="small" sx={{ ml: 2 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="type-label">Validation Type</InputLabel>
              <Select
                labelId="type-label"
                value={type === 'yesno' ? 'Yes / No' : 'Multiple choice'}
                label="Validation Type"
                onChange={e => setType(e.target.value === 'Yes / No' ? 'yesno' : 'multiplechoice')}
              >
                <MenuItem value="Yes / No">Yes / No</MenuItem>
                <MenuItem value="Multiple choice">Multiple choice</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Question" value={question} onChange={e => setQuestion(e.target.value)} fullWidth multiline minRows={2} sx={{ mt: 1 }} />
            {type === 'yesno' && (
              <FormControl sx={{ mt: 2 }}>
                <RadioGroup row value={yesNoAnswer ? 'Yes' : 'No'} onChange={e => setYesNoAnswer(e.target.value === 'Yes')}>
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            )}
            {type === 'multiplechoice' && (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {choices.map((choice: string, idx: number) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Radio
                      checked={correctChoice === idx}
                      onChange={() => setCorrectChoice(idx)}
                      value={idx}
                    />
                    <TextField
                      value={choice}
                      onChange={e => handleChoiceChange(idx, e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder={`Choice ${idx + 1}`}
                    />
                    <IconButton size="small" onClick={() => handleRemoveChoice(idx)} disabled={choices.length <= 1}><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                ))}
                <Button onClick={handleAddChoice} size="small" sx={{ mt: 1, alignSelf: 'flex-start' }} startIcon={<AddIcon />}>Add Choice</Button>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end', pr: 3, pb: 2 }}>
        <Button variant="contained" onClick={handleSave} disabled={!question.trim() || (type === 'multiplechoice' && choices.some((c: string) => !c.trim()))}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionView;
